import { posthog } from '@lib/posthog';
import { FFH_ROLES } from '@lib/constants';
import { isUserAuthenticated, ROLE_LEVELS } from '@lib/utils';
import type { Auth0UserInfo } from '@lib/auth0/utils';
import type { LeagueContext } from '@api-service/league-context';
import type { AnalyticsMembershipTier } from '@lib/analytics/events';

/**
 * FFH-2371 — shared super properties, attached to every PostHog event.
 *
 * Registered once and re-registered on change, so every captured event is
 * segmentable by tier, auth state, FPL link and gameweek for free. `platform`
 * is an explicit product value shared with the app; `$lib` is an SDK detail
 * and must not be used for platform splits. The UTM split (FFH-1899, see
 * `posthog-utm.ts`) is a separate family and stays intact alongside these.
 *
 * Never duplicate feature flag state here: posthog-js already stamps active
 * flags on every event as `$feature/<key>` properties.
 */
export type SharedSuperProperties = {
  platform: 'web';
  membership_tier: AnalyticsMembershipTier;
  is_logged_in: boolean;
  has_fpl_id: boolean;
  gameweek: number | null;
};

export type SharedSuperPropertiesInput = {
  user: Auth0UserInfo | undefined | null;
  profile: { fplTeamId: number | null } | undefined | null;
  leagueContext: LeagueContext | null;
};

const MEMBERSHIP_TIER_BY_ROLE: Record<string, AnalyticsMembershipTier> = {
  FREE: 'free',
  STARTER: 'starter',
  PRO: 'pro',
  ULTRA: 'ultra'
};

const ROLE_LEVEL_BY_ROLE = ROLE_LEVELS as Record<string, number>;

/**
 * Lowercase membership tier from the Auth0 roles claim, highest role wins.
 * Lowercase is the analytics contract (FFH-2371); CleverTap's capitalised
 * names predate it and stay as they are. Roles outside ROLE_LEVELS (e.g.
 * `admin`) are not membership tiers and resolve to `free`.
 *
 * Exported for event properties that need the visitor's CURRENT tier (e.g.
 * subscription_cancel_started); do not confuse with getCleverTapTierName,
 * which returns the capitalised CleverTap vocabulary.
 */
export const getAnalyticsMembershipTier = (
  user: Auth0UserInfo | undefined | null
): AnalyticsMembershipTier => {
  const roles = user?.[FFH_ROLES] ?? [];
  const highestRole = roles.reduce(
    (highest, role) =>
      (ROLE_LEVEL_BY_ROLE[role] ?? -1) > (ROLE_LEVEL_BY_ROLE[highest] ?? -1)
        ? role
        : highest,
    'FREE'
  );
  return MEMBERSHIP_TIER_BY_ROLE[highestRole] ?? 'free';
};

/** Pure builder: the full shared property map for the current visitor. */
export const buildSharedSuperProperties = ({
  user,
  profile,
  leagueContext
}: SharedSuperPropertiesInput): SharedSuperProperties => ({
  platform: 'web',
  membership_tier: getAnalyticsMembershipTier(user),
  is_logged_in: isUserAuthenticated(user),
  has_fpl_id: profile?.fplTeamId != null,
  gameweek: leagueContext?.currentGameweek?.number ?? null
});

/**
 * Registers the shared properties. Idempotent and safe to re-run — every key
 * always has a value, so a fresh register simply overwrites the previous one.
 * Registering itself captures nothing, so it is safe pre-consent (capture
 * stays opted out and persistence is in-memory until consent), and it must be
 * re-run after any `posthog.reset()` (consent grant, logout), which wipes
 * registered super properties.
 */
export const registerSharedSuperProperties = (
  input: SharedSuperPropertiesInput
): void => {
  if (typeof window === 'undefined') return;

  posthog.register(buildSharedSuperProperties(input));
};
